import React, { useEffect, useRef } from "react";
import { useUser } from "../contexts/UserContext";
import { useNavigate, useParams } from "react-router-dom";

import Title from "../components/Title";
import { NavigationText, SummaryText } from "../Text";
import "../printable-content.css";
import CumulativeCashFlowGraph from "../components/CumulativeCashFlowGraph";
import CostBenefitGraph from "../components/CostBenefitGraph";
import Chart from "chart.js/auto";
import { Button } from "@mui/base";
import { Tooltip } from "@mui/material";
import { FaPrint } from "react-icons/fa";
import { BsArrowLeft } from "react-icons/bs";

import { GET_USER_SUMMARIES_BY_ID } from "../apollo-queries";
import { useQuery } from "@apollo/client";

const PrintableContent: React.FC = () => {
  const { user } = useUser();
  // retrieve uuid from the URL
  const { uuid } = useParams<{ uuid: string }>();
  const userIdString: string = uuid!;
  user.uuid = userIdString;

  const { data, loading, error, refetch } = useQuery(GET_USER_SUMMARIES_BY_ID, {
    variables: { user_id: user.uuid },
  });

  let formatCurrency = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

  useEffect(() => {
    refetch();
  }, [data, refetch]);

  // parse roi to string and handle null case
  let roiString = "";
  if (data?.summaries[0]?.roiValid === true) {
    roiString = String(Math.round(data?.summaries[0].roi * 1000) / 10) + "%";
  } else {
    roiString = "N/A";
  }

  // parse irr to string and handle null case
  let irrString = "";
  if (data?.summaries[0]?.irrValid === true) {
    irrString =
      String(Math.round(data?.summaries[0].annualIrr * 1000) / 10) + "%";
  } else {
    irrString = "N/A";
  }

  if (loading) return <div>Loading...</div>;

  if (error) return <div>Error! {error.message}</div>;

  return (
    <div className="printable-content">
      <div className="mb-10">
        <Title title={SummaryText.title} subTitle={SummaryText.subTitle} />
      </div>
      {/* Objective section. renders if the user put in an objective */}
      {data?.objectives[0]?.description === "" ? null : (
        <div className="flex flex-col mb-5">
          <label className="w-full border-b-2 border-UDig-orange pb-1 px-1 font-sans font-bold text-UDig-orange text-2xl">
            {SummaryText.objective}
          </label>
          {/* div below is necessary to make the orange underline span the whole page instead of using border */}
          <div className="border-b-1 border-UDig-orange"></div>
          <div className="my-2 p-6 rounded-md bg-white drop-shadow-lg text-UDig-gray whitespace-normal">
            {data?.objectives[0]?.description}
          </div>
        </div>
      )}
      {/* Analysis 7 boxes section */}
      <div className="space-y-1 mb-5">
        <label className="w-full px-1 font-sans font-bold text-UDig-orange text-2xl">
          {SummaryText.analysis.label}
        </label>
        <div className="w-full border-t-2 border-UDig-orange pb-1"></div>
        <div className="mt-2 space-y-4 text-UDig-gray text-center grid grid-cols-6 gap-x-4 gap-y-2">
          {/* Project Cost */}
          <Tooltip
            title={
              <>
                <h3 className="font-bold">
                  {SummaryText.analysis.projectCost.label}
                </h3>
                <div>{SummaryText.analysis.projectCost.tooltip}</div>
              </>
            }
            placement="bottom"
            arrow
          >
            <div className="p-6 mt-4 rounded-md bg-white drop-shadow-lg flex flex-col items-center col-span-2">
              <div>
                {formatCurrency.format(
                  Math.round(data?.summaries[0]?.totalCost)
                )}
              </div>
              <div className="text-lg font-semibold">
                {SummaryText.analysis.projectCost.label}
              </div>
            </div>
          </Tooltip>
          {/* Benefits */}
          <Tooltip
            title={
              <>
                <h3 className="font-bold">
                  {SummaryText.analysis.benefits.label}
                </h3>
                <div>{SummaryText.analysis.benefits.tooltip}</div>
              </>
            }
            placement="bottom"
            arrow
          >
            <div className="p-6 rounded-md bg-white drop-shadow-lg flex flex-col items-center col-span-2">
              <div>
                {formatCurrency.format(
                  Math.round(data?.summaries[0]?.totalBenefit)
                )}
              </div>
              <div className="text-lg font-semibold">
                {SummaryText.analysis.benefits.label}
              </div>
            </div>
          </Tooltip>
          {/* Net Profit */}
          <Tooltip
            title={
              <>
                <h3 className="font-bold">
                  {SummaryText.analysis.netProfit.label}
                </h3>
                <div>{SummaryText.analysis.netProfit.tooltip}</div>
              </>
            }
            placement="bottom"
            arrow
          >
            <div className="p-6 rounded-md bg-white drop-shadow-lg flex flex-col items-center col-span-2">
              <div>
                {formatCurrency.format(
                  Math.round(data?.summaries[0]?.netProfit)
                )}
              </div>
              <div className="text-lg font-semibold">
                {SummaryText.analysis.netProfit.label}
              </div>
            </div>
          </Tooltip>
          {/* Net Present Value */}
          <Tooltip
            title={
              <>
                <h3 className="font-bold">
                  {SummaryText.analysis.netPresentValue.label}
                </h3>
                <div>{SummaryText.analysis.netPresentValue.tooltip}</div>
              </>
            }
            placement="bottom"
            arrow
          >
            <div className="p-6 rounded-md bg-white drop-shadow-lg flex flex-col items-center col-span-3">
              <div>
                {formatCurrency.format(
                  Math.round(data?.summaries[0]?.netPresentValue)
                )}
              </div>
              <div className="text-lg font-semibold">
                {SummaryText.analysis.netPresentValue.label}
              </div>
            </div>
          </Tooltip>
          {/* Payback Period */}
          <Tooltip
            title={
              data?.summaries[0]?.payBackPeriod === "None" ? (
                <>
                  <h3 className="font-bold">
                    {SummaryText.analysis.paybackPeriod.error.label}
                  </h3>
                  <div>{SummaryText.analysis.paybackPeriod.error.message}</div>
                </>
              ) : (
                <>
                  <h3 className="font-bold">
                    {SummaryText.analysis.paybackPeriod.label}
                  </h3>
                  <div>{SummaryText.analysis.paybackPeriod.tooltip}</div>
                </>
              )
            }
            placement="bottom"
            arrow
          >
            <div className="p-6 rounded-md bg-white drop-shadow-lg flex flex-col items-center col-span-3">
              <div>{data?.summaries[0]?.payBackPeriod}</div>
              <div className="text-lg font-semibold">
                {SummaryText.analysis.paybackPeriod.label}
              </div>
            </div>
          </Tooltip>
          {/* ROI */}
          <Tooltip
            title={
              roiString === "N/A" ? (
                <>
                  <h3 className="font-bold">
                    {SummaryText.analysis.ROI.error.label}
                  </h3>
                  <div>{SummaryText.analysis.ROI.error.message}</div>
                </>
              ) : (
                <>
                  <h3 className="font-bold">
                    {SummaryText.analysis.ROI.label}
                  </h3>
                  <div>{SummaryText.analysis.ROI.tooltip}</div>
                </>
              )
            }
            placement="bottom"
            arrow
          >
            <div className="p-6 rounded-md bg-white drop-shadow-lg flex flex-col items-center col-span-3">
              <div>{roiString}</div>
              <div className="text-lg font-semibold">
                {SummaryText.analysis.ROI.label}
              </div>
            </div>
          </Tooltip>
          {/* IRR */}
          <Tooltip
            title={
              irrString === "N/A" ? (
                <>
                  <h3 className="font-bold">
                    {SummaryText.analysis.IRR.error.label}
                  </h3>
                  <div>{SummaryText.analysis.IRR.error.message}</div>
                </>
              ) : (
                <>
                  <h3 className="font-bold">
                    {SummaryText.analysis.IRR.label}
                  </h3>
                  <div>{SummaryText.analysis.IRR.tooltip}</div>
                </>
              )
            }
            placement="bottom"
            arrow
          >
            <div className="p-6 rounded-md bg-white drop-shadow-lg flex flex-col items-center col-span-3">
              <div>{irrString}</div>
              <div className="text-lg font-semibold">
                {SummaryText.analysis.IRR.label}
              </div>
            </div>
          </Tooltip>
        </div>
      </div>
      {/* Financials */}
      <div className="flex flex-col">
        <label className="w-full mb-1 px-1 font-sans font-bold text-UDig-orange text-2xl">
          {SummaryText.financials.label}
        </label>
        <div className="w-full border-t-2 border-UDig-orange pb-1"></div>
        <div className="mt-4 p-6 rounded-md bg-white drop-shadow-lg font-bold text-UDig-gray text-center">
          <div className="grid grid-cols-9 space-x-4 justify-items-end font-semibold items-end">
            <div />
            <div className="col-span-2 text-right">
              {SummaryText.financials.totalCosts}
            </div>
            <div className="col-span-2 text-right">
              {SummaryText.financials.totalBenefits}
            </div>
            <div className="col-span-2 text-right">
              {SummaryText.financials.netProfit}
            </div>
            <div className="col-span-2 text-right">
              {SummaryText.financials.discountedNetProfit}
            </div>
          </div>
          {/* Displays actual financials numbers matrix */}
          {Array.from(
            { length: data?.summaries[0]?.maxYears },
            (_, yearIndex) => (
              <div className="grid grid-cols-9 space-x-4 justify-items-end font-normal">
                <div className="whitespace-nowrap">
                  {SummaryText.financials.year} {yearIndex + 1}
                </div>
                <div className="col-span-2">
                  {formatCurrency.format(
                    Math.round(data?.summaries[0]?.totalCostArray[yearIndex])
                  )}
                </div>
                <div className="col-span-2">
                  {formatCurrency.format(
                    Math.round(data?.summaries[0]?.totalBenefitArray[yearIndex])
                  )}
                </div>
                <div className="col-span-2">
                  {formatCurrency.format(
                    Math.round(data?.summaries[0]?.netProfitArray[yearIndex])
                  )}
                </div>
                <div className="col-span-2">
                  {formatCurrency.format(
                    Math.round(data?.summaries[0]?.discountedNbArray[yearIndex])
                  )}
                </div>
              </div>
            )
          )}
          {/* Displays totals */}
          <div className="grid grid-cols-9 space-x-4 justify-items-end font-semibold border-UDig-gray border-t-2">
            <div>{SummaryText.financials.total}</div>
            <div className="col-span-2">
              {formatCurrency.format(Math.round(data?.summaries[0]?.totalCost))}
            </div>
            <div className="col-span-2">
              {formatCurrency.format(
                Math.round(data?.summaries[0]?.totalBenefit)
              )}
            </div>
            <div className="col-span-2">
              {formatCurrency.format(Math.round(data?.summaries[0]?.netProfit))}
            </div>
            <div className="col-span-2">
              {formatCurrency.format(
                Math.round(data?.summaries[0]?.discountedNbArraySum)
              )}
            </div>
          </div>
        </div>
      </div>
      <div>
        <h2 className="pb-1 mb-1 border-b-2 border-UDig-orange px-1 font-sans font-bold text-UDig-orange text-2xl mt-6">
          {SummaryText.cumulativeCashFlow}
        </h2>
        <CumulativeCashFlowGraph data={data} />
      </div>
      <div>
        <h2 className="pb-1 mb-1 border-b-2 border-UDig-orange px-1 font-sans font-bold text-UDig-orange text-2xl mt-6">
          {SummaryText.costsBenefitsGraph}
        </h2>
        <CostBenefitGraph data={data} />
      </div>
      {/* Qualitative Benefits */}
      {data?.qualitative_benefits[0]?.category === "" &&
      data?.qualitative_benefits[0]?.benefits[0] === "" ? null : (
        <div className="space-y-4">
          <div>
            <label className="w-full pb-1 px-1 font-sans font-bold text-UDig-orange text-2xl">
              {SummaryText.benefits}
            </label>
            <div className="w-full border-t-2 border-UDig-orange pb-1"></div>
            <div>
              {data.qualitative_benefits.map((item) => {
                return (
                  <div className="my-2 p-6 rounded-md bg-white drop-shadow-lg flex flex-col">
                    <div className="text-UDig-gray text-lg font-semibold">
                      {item.category}
                    </div>
                    <ul className="ml-5 list-disc">
                      {item.benefits.map((benefit) => {
                        return <li className="text-UDig-gray">{benefit}</li>;
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  // retrieve uuid from the URL
  const { uuid } = useParams<{ uuid: string }>();
  const userIdString: string = uuid!;
  if (userIdString !== undefined) {
    user.uuid = userIdString;
  }

  window.onbeforeprint = function () {
    for (const id in Chart.instances) {
      Chart.instances[id].resize(624, 624); // 624 = width of letter paper in pixels w/ a 1 inch margin
    }
  };

  const Print = () => {
    if (summaryPageRef.current) {
      // Adjust the height of the summary page to fit its content
      const summaryPageHeight = summaryPageRef.current.scrollHeight;
      summaryPageRef.current.style.height = `${summaryPageHeight}px`;
    }
    window.print();
    for (const id in Chart.instances) {
      Chart.instances[id].resize(); // resets chart sizes to default
    }
  };

  const handlePrevious = () => {
    navigate(`/Review/${user.uuid} `);
  };

  const summaryPageRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className="mb-20 px-10 tablet:px-20 w-90 m-auto"
      id="summary-page"
      ref={summaryPageRef}
    >
      <form className="w-full h-1/2 items-center pt-10">
        <PrintableContent />
        <span className="mt-4 mb-10 flex justify-between text-UDig-orange">
          <Button
            className="flex items-center exclude-element"
            onClick={handlePrevious}
          >
            <BsArrowLeft className="mr-2" />
            {NavigationText.previous}
          </Button>
          <Button className="flex items-center exclude-element" onClick={Print}>
            {SummaryText.printButton}
            <FaPrint className="ml-2 exclude-element" />
          </Button>
        </span>
      </form>
    </div>
  );
};

export default SummaryScreen;
